import { useEffect, useState, useRef, useMemo } from "react";
import { Mail, Linkedin, Globe, Calendar, MoreVertical, Trash2, Edit3, Archive, Send, X, SquarePlus, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import { auth, db } from "../firebase";
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp, query, orderBy } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavbar } from "./DashboardLayout";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import AddContactModal, { Contact, EmailStatus } from "../components/addContact";

const SECTION_STORAGE_KEY = "coldEmailCollapsedSections";
const SECTION_KEYS = ["draft", "sent", "replied"] as const;
type SectionKey = typeof SECTION_KEYS[number];

function StatusBadge({ status }: { status: EmailStatus }) {
  const styles: Record<EmailStatus, string> = {
    draft: "bg-amber-100 text-amber-700 border-amber-200",
    sent: "bg-blue-100 text-blue-700 border-blue-200",
    replied: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  const labels: Record<EmailStatus, string> = {
    draft: "Draft",
    sent: "Sent",
    replied: "Replied",
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-md border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function EmailEditorModal({ 
  contact, 
  onClose, 
  onSave 
}: { 
  contact: Contact; 
  onClose: () => void; 
  onSave: (content: string, status: EmailStatus) => void;
}) {
  const [content, setContent] = useState(contact.emailContent || "");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(content.length, content.length);
    }
  }, []);

  const handleSave = async (newStatus: EmailStatus) => {
    setSaving(true);
    await onSave(content, newStatus);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showClose={false}
        className="flex max-w-4xl flex-col gap-0 overflow-hidden p-0 shadow-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-cocoa/20 px-6 py-4 flex items-center justify-between">
          <div>
            <DialogTitle className="text-xl">Email to {contact.name}</DialogTitle>
            <DialogDescription className="mt-1">{contact.role}</DialogDescription>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-cocoa hover:bg-cocoa/10"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your cold email here..."
            className="w-full h-full min-h-[400px] px-4 py-3 rounded-lg border-2 border-cocoa/20 focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition resize-none font-sans text-[15px] leading-relaxed"
          />
        </div>

        <div className="border-t border-cocoa/20 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-cocoa">
            {content.length} characters
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleSave("draft")}   
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-cocoa/50 text-espresso hover:bg-cocoa/5 disabled:opacity-50"
            >
              Save Draft
            </button>
            <button
              onClick={() => handleSave("sent")}
            //   disabled={saving || !content.trim()}
              className="px-4 py-2 rounded-lg bg-caramel text-white hover:opacity-90  flex items-center gap-2"
            >
              <Send size={16} />
              Mark as Sent
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactCard({ 
  contact, 
  onEdit, 
  onDelete, 
  onArchive,
  onOpenEditor, 
  onStatusChange
}: { 
  contact: Contact; 
  onEdit: () => void;
  onDelete: () => void;
  onArchive: () => void;
  onOpenEditor: () => void;
  onStatusChange: (status: EmailStatus) => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setShowStatusMenu(false);
      }
    }
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const emailPreview = contact.emailContent
    ? contact.emailContent.split('\n')[0].substring(0, 60) + (contact.emailContent.length > 60 ? '...' : '')
    : 'No content yet';

  return (
    <div 
      className="rounded-2xl border border-cocoa/10 bg-cream p-4 shadow-sm hover:shadow-md transition cursor-pointer"
      onClick={onOpenEditor}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-[15px] font-semibold text-espresso">{contact.name}</h3>
          <p className="text-sm text-cocoa">{contact.role}</p>
        </div>
        
        <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded-md text-cocoa hover:bg-oat"
          >
            <MoreVertical size={16} />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-cocoa/10 py-1 z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
              >
                <Edit3 size={14} />
                Edit Details
              </button>
             
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
              >
                <Archive size={14} />
                {contact.archived ? 'Unarchive' : 'Archive'}
              </button>
             
             
              <div
                className="relative group"
                onMouseEnter={() => setShowStatusMenu(true)}
                // onMouseLeave={() => setShowStatusMenu(false)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStatusMenu(!showStatusMenu);
                  }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
                >
                  <span className="flex items-center gap-2">
                    <ChevronRight size={14} />
                    Edit Status
                  </span>
                  <ChevronUp size={14} className={`transition-transform ${showStatusMenu ? 'rotate-180' : 'rotate-90'}`} />
                </button>
                
                
                {showStatusMenu && (
                  <div className="absolute left-full top-0 ml-1 w-32 bg-white rounded-lg shadow-lg border border-cocoa/10 py-1 z-20">
                    {(["draft", "sent", "replied"] as EmailStatus[]).map((statusOption) => (
                      <button
                        key={statusOption}
                        onClick={(e) => {
                          e.stopPropagation();
                          onStatusChange(statusOption);
                          setShowMenu(false);
                          setShowStatusMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-cocoa/5 ${
                          contact.status === statusOption ? 'text-caramel hover:bg-cocoa/5 font-medium' : 'text-espresso'
                        }`}
                      >
                        {statusOption === 'draft' && 'Draft'}
                        {statusOption === 'sent' && 'Sent'}
                        {statusOption === 'replied' && 'Replied'}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {contact.email && (
          <div className="flex items-center gap-2 text-sm text-cocoa">
            <Mail size={14} className="shrink-0" />
            <span className="truncate">{contact.email}</span>
          </div>
        )}
        {contact.linkedin && (
          <div className="flex items-center gap-2 text-sm text-cocoa">
            <Linkedin size={14} className="shrink-0" />
            <a 
              href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:text-caramel"
              onClick={(e) => e.stopPropagation()}
            >
              LinkedIn
            </a>
          </div>
        )}
        {contact.website && (
          <div className="flex items-center gap-2 text-sm text-cocoa">
            <Globe size={14} className="shrink-0" />
            <a 
              href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:text-caramel"
              onClick={(e) => e.stopPropagation()}
            >
              Website
            </a>
          </div>
        )}
      </div>

      {contact.emailContent && (
        <div className="mt-3 p-2 bg-white rounded-lg">
          <p className="text-xs text-cocoa italic line-clamp-1">
            "{emailPreview}"
          </p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <StatusBadge status={contact.status} />
        {contact.sentDate && (
          <div className="flex items-center gap-1.5 text-xs text-cocoa">
            <Calendar size={12} />
            <span>{new Date(contact.sentDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          </div>
        )}
      </div>
    </div>
  );
}


export default function ColdEmailing() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const { setNavbarActions } = useNavbar();
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    draft: false,
    sent: false,
    replied: false,
  });
  const persistedSectionsRef = useRef<Set<SectionKey>>(new Set<SectionKey>());

  
  const [user, setUser] = useState(auth.currentUser);

 
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser); 
      if (!currentUser) {
        setLoading(false); 
      }
    });

    return () => unsubscribeAuth();
  }, []); 

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(SECTION_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Partial<Record<string, boolean>>;
      const restored: Partial<Record<SectionKey, boolean>> = {};
      SECTION_KEYS.forEach((key) => {
        if (typeof parsed?.[key] === "boolean") {
          restored[key] = parsed[key];
          persistedSectionsRef.current.add(key);
        }
      });
      if (Object.keys(restored).length > 0) {
        setCollapsedSections((prev) => ({ ...prev, ...restored }));
      }
    } catch (error) {
      console.error("Failed to restore section preferences", error);
    }
  }, []);

  useEffect(() => {
    setNavbarActions(
      <button
        onClick={() => {
            setEditingContact(null);
            setShowAddModal(true);
          }}
        className="inline-flex items-center gap-2 rounded-lg bg-caramel px-3 py-1 text-white hover:opacity-90 transition"
      >
        <SquarePlus size={16} />
        Add Contact
      </button>
    );

    return () => setNavbarActions(null);
}, []);

 
  useEffect(() => {
    if (!user) {
      setContacts([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, "users", user.uid, "coldEmails"),
      orderBy("createdAt", "desc")
    );


    const unsubscribe = onSnapshot(q, (snapshot) => {
      const contactsData: Contact[] = [];
      
      snapshot.forEach((doc) => {
       
        contactsData.push({ id: doc.id, ...doc.data() } as Contact);
      });
      
      setContacts(contactsData); 
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(collapsedSections));
  }, [collapsedSections]);

  const handleAddContact = async (contactData: Omit<Contact, "id" | "createdAt">) => {
    const user = auth.currentUser;
    if (!user) return; 

    try {

      const cleanedData: any = {
        name: contactData.name,
        role: contactData.role,
        status: contactData.status,
        emailContent: contactData.emailContent,
        archived: contactData.archived,
      };
      
      if (contactData.email) cleanedData.email = contactData.email;
      if (contactData.linkedin) cleanedData.linkedin = contactData.linkedin;
      if (contactData.website) cleanedData.website = contactData.website;
      if (contactData.sentDate) cleanedData.sentDate = contactData.sentDate;

      if (editingContact) {
      
        await updateDoc(doc(db, "users", user.uid, "coldEmails", editingContact.id), cleanedData);
        setEditingContact(null);
      } else {
       
        await addDoc(collection(db, "users", user.uid, "coldEmails"), {
          ...cleanedData,
          createdAt: serverTimestamp(), 
        });
      }
    } catch (error) {
      console.error("Error adding/updating contact:", error);
      alert("Failed to save contact");
    }
   
  };

  const handleDeleteContact = async (contactId: string) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
   
      await deleteDoc(doc(db, "users", user.uid, "coldEmails", contactId));
    } catch (error) {
      console.error("Error deleting contact:", error);
      alert("Failed to delete contact");
    }
  };

  const handleArchiveContact = async (contact: Contact) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // updateDoc() updates just the archived field
      // We flip the boolean value: if archived is true, make it false, and vice versa
      await updateDoc(doc(db, "users", user.uid, "coldEmails", contact.id), {
        archived: !contact.archived,
      });
    } catch (error) {
      console.error("Error archiving contact:", error);
      alert("Failed to archive contact");
    }
  };

    const handleChangeStatus = async (contact: Contact, status: EmailStatus) => {
    const user = auth.currentUser;
    if (!user) return;

    if (contact.status === status) {
      return;
    }

    try {
      const updateData: any = {
        status,
      };

      if (status === "sent" && !contact.sentDate) {
        updateData.sentDate = new Date().toISOString();
      }

      await updateDoc(doc(db, "users", user.uid, "coldEmails", contact.id), updateData);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  // FUNCTION: Save email content and update status
  const handleSaveEmail = async (contactId: string, content: string, status: EmailStatus) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Build an object with the fields we want to update
      const updateData: any = {
        emailContent: content,
        status: status,
       
      };

      // If marking as sent for the first time, add a sentDate
      if (status === "sent" && !selectedContact?.sentDate) {
        updateData.sentDate = new Date().toISOString();
      }

      // Update the document with our new data
      await updateDoc(doc(db, "users", user.uid, "coldEmails", contactId), updateData);
    } catch (error) {
      console.error("Error saving email:", error);
      alert("Failed to save email");
    }
  };


  const activeContacts = useMemo(() => {
    return contacts.filter(c => !c.archived);
  }, [contacts]);

  const archivedContacts = useMemo(() => {
    return contacts.filter(c => c.archived);
  }, [contacts]);

  const groupedContacts = useMemo(() => {
    const draft = activeContacts.filter(c => c.status === "draft");
    const sent = activeContacts.filter(c => c.status === "sent");
    const replied = activeContacts.filter(c => c.status === "replied");
    return { draft, sent, replied };
  }, [activeContacts]);

   useEffect(() => {
    setCollapsedSections((prev) => {
      let changed = false;
      const next = { ...prev };
      const sections: [SectionKey, number][] = [
        ["draft", groupedContacts.draft.length],
        ["sent", groupedContacts.sent.length],
        ["replied", groupedContacts.replied.length],
      ];

      sections.forEach(([key, count]) => {
        if (!persistedSectionsRef.current.has(key)) {
          const desiredState = count === 0;
          if (prev[key] !== desiredState) {
            next[key] = desiredState;
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [groupedContacts.draft.length, groupedContacts.sent.length, groupedContacts.replied.length, archivedContacts.length]);

  const toggleSection = (key: SectionKey) => {
    persistedSectionsRef.current.add(key);
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="w-full">
    
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-cocoa">Loading contacts...</div>
        </div>
      ) : (
        <div className="space-y-8">
        {groupedContacts.draft.length > 0 && (
          <section>
            <button
              type="button"
              onClick={() => toggleSection("draft")}
              className="flex w-full items-center justify-between mb-4"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
                <h2 className="text-[15px] font-semibold text-espresso">
                  Drafts <span className="text-cocoa font-normal">({groupedContacts.draft.length})</span>
                </h2>
              </div>
              <ChevronDown
                size={16}
                className={`text-cocoa transition-transform ${collapsedSections.draft ? "-rotate-90" : ""}`}
              />
            </button>
            {!collapsedSections.draft && (
              groupedContacts.draft.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-sand text-cocoa flex items-center justify-center h-32">
                  No drafts yet
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedContacts.draft.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      onEdit={() => {
                        setEditingContact(contact);
                        setShowAddModal(true);
                      }}
                      onDelete={() => handleDeleteContact(contact.id)}
                      onArchive={() => handleArchiveContact(contact)}
                      onOpenEditor={() => {
                        setSelectedContact(contact);
                        setShowEmailEditor(true);
                      }}
                     onStatusChange={(status) => handleChangeStatus(contact, status)}

                    />
                  ))}
                </div>
              )
            )}
          </section>
        )}
        {groupedContacts.sent.length > 0 && (
          <section>
                 <button
              type="button"
              onClick={() => toggleSection("sent")}
              className="flex w-full items-center justify-between mb-4"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-500" />
                <h2 className="text-[15px] font-semibold text-espresso">
                  Sent <span className="text-cocoa font-normal">({groupedContacts.sent.length})</span>
                </h2>
              </div>
              <ChevronRight
                size={16}
                className={`text-cocoa transition-transform ${!collapsedSections.sent ? "rotate-90" : ""}`}
              />
            </button>
            {!collapsedSections.sent && (
              groupedContacts.sent.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-sand text-cocoa flex items-center justify-center h-32">
                  No sent emails yet
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedContacts.sent.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      onEdit={() => {
                        setEditingContact(contact);
                        setShowAddModal(true);
                      }}
                      onDelete={() => handleDeleteContact(contact.id)}
                      onArchive={() => handleArchiveContact(contact)}
                      onOpenEditor={() => {
                        setSelectedContact(contact);
                        setShowEmailEditor(true);
                      }}
                     onStatusChange={(status) => handleChangeStatus(contact, status)}

                    />
                  ))}
                </div>
              )
            )}
          </section>
        )}

        {groupedContacts.replied.length > 0 && (
          <section>
                 <button
              type="button"
              onClick={() => toggleSection("replied")}
              className="flex w-full items-center justify-between mb-4"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <h2 className="text-[15px] font-semibold text-espresso">
                  Replied <span className="text-cocoa font-normal">({groupedContacts.replied.length})</span>
                </h2>
              </div>
              <ChevronRight
                size={16}
                className={`text-cocoa transition-transform ${!collapsedSections.replied ? "rotate-90" : ""}`}
              />
            </button>
            {!collapsedSections.replied && (
              groupedContacts.replied.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-sand text-cocoa flex items-center justify-center h-32">
                  No replies yet
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedContacts.replied.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      onEdit={() => {
                        setEditingContact(contact);
                        setShowAddModal(true);
                      }}
                      onDelete={() => handleDeleteContact(contact.id)}
                      onArchive={() => handleArchiveContact(contact)}
                      onOpenEditor={() => {
                        setSelectedContact(contact);
                        setShowEmailEditor(true);
                      }}
                    onStatusChange={(status) => handleChangeStatus(contact, status)}

                    />
                  ))}
                </div>
              )
            )}
          </section>
        )}

          {archivedContacts.length > 0 && (
            <section>
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-2 mb-4 text-cocoa hover:text-espresso transition"
              >
                <Archive size={16} />
                <h2 className="text-[15px] font-semibold">
                  Archived <span className="font-normal">({archivedContacts.length})</span>
                </h2>
                <ChevronRight size={16} className={`transition-transform ${showArchived ? 'rotate-90' : ''}`} />
              </button>
              {showArchived && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-70">
                  {archivedContacts.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      onEdit={() => {
                        setEditingContact(contact);
                        setShowAddModal(true);
                      }}
                      onDelete={() => handleDeleteContact(contact.id)}
                      onArchive={() => handleArchiveContact(contact)}
                      onOpenEditor={() => {
                        setSelectedContact(contact);
                        setShowEmailEditor(true);
                      }}
                      onStatusChange={(status) => handleChangeStatus(contact, status)}

                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      <AddContactModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingContact(null);
        }}
        onAdd={handleAddContact}
        editContact={editingContact || undefined}
      />

      {showEmailEditor && selectedContact && (
        <EmailEditorModal
          contact={selectedContact}
          onClose={() => {
            setShowEmailEditor(false);
            setSelectedContact(null);
          }}
          onSave={(content, status) => handleSaveEmail(selectedContact.id, content, status)}
        />
      )}
    </div>
  );
}